'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { FlameS } from '@lichtblick/photon/icons/energy/flame-s';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { type ChangeEventHandler, type FC, useCallback, useEffect, useState } from 'react';
import type { CalculatorGasType } from '../../types/storyblok';
import { CityInput } from '../OAllInOneCalculator/CityInput';
import {
  ELECTRICITY_USAGE,
  gasUsagePerSquareMeter,
  squareMeterLimit,
  usageLimitElectricity,
  usageLimitGas,
} from '../OAllInOneCalculator/constants';
import { UsageModal } from '../OAllInOneCalculator/UsageModal';
import { useVpCodes } from '../OAllInOneCalculator/useVpCodes';
import styles from './calculator.module.scss';

export const CalculatorGas: FC<CalculatorGasType> = () => {
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');

  const [people, setPeople] = useState(1);
  const [electricityUsage, setElectricityUsage] = useState(String(ELECTRICITY_USAGE[0]));

  const [addElectricity, setAddElectricity] = useState(false);
  const [space, setSpace] = useState(squareMeterLimit.default);
  const [gasUsage, setGasUsage] = useState(String(space * gasUsagePerSquareMeter));

  const [electricityUsageModalOpen, setElectricityUsageModalOpen] = useState(false);
  const [gasUsageModalOpen, setGasUsageModalOpen] = useState(false);

  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();

  const cityChangeHandler = useCallback((city: string, zip: string) => {
    setCity(city);
    setZip(zip);
  }, []);

  const electricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setElectricityUsage(e.currentTarget.value);
  }, []);

  const addElectricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setAddElectricity(e.currentTarget.checked);
  }, []);

  const gasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setGasUsage(e.currentTarget.value);
  }, []);

  const [vpCodes, setVpCodes] = useState<{
    uvp?: string;
    vp?: string;
  }>({});

  useEffect(() => {
    setVpCodes(computeVpCodes());
  }, [computeVpCodes]);

  return (
    <>
      <form
        action="/checkout/"
        className={styles.form}
        id="gas-calculator"
        method="GET"
        onSubmit={(e) => {
          if (Number(gasUsage) > usageLimitGas.max) {
            e.preventDefault();
            setGasUsageModalOpen(true);

            return;
          }

          if (addElectricity && Number(electricityUsage) > usageLimitElectricity.max) {
            e.preventDefault();
            setElectricityUsageModalOpen(true);

            return;
          }

          if (e.currentTarget.checkValidity()) {
            trackCustomEvent({
              eventName: 'tariff_calculator_show_tariffs',
              customProps: {
                product: 'ÖkoGas',
                zip_code: zip,
                living_space: `${space} m²`,
                consumption_calculator: `${gasUsage} kWh/Jahr`,
                bundle_product: addElectricity,
              },
            });
          }
        }}
      >
        <Headline className={styles.headline} renderAs="h4" size="s">
          Gas
          <FlameS />
        </Headline>
        <CityInput onChange={cityChangeHandler} />
        <Stepper
          defaultValue={space}
          label="Wohnefläche in m²"
          max={squareMeterLimit.max}
          min={squareMeterLimit.min}
          onChange={(value) => {
            setSpace(value);
            setGasUsage(String(value * gasUsagePerSquareMeter));
          }}
          step={squareMeterLimit.step}
        />
        <Textfield
          label="Gasverbrauch"
          min={usageLimitGas.min}
          name="gas"
          errorMessage={`Der Verbrauch muss über ${usageLimitGas.min} kWh/Jahr liegen.`}
          onChange={gasChangeHandler}
          rightSlot={
            <Text className={styles.unit} size="s">
              kWh/Jahr
            </Text>
          }
          required
          type="number"
          value={gasUsage}
        />
        <Checkbox label={<Checkbox.Label>ÖkoStrom hinzufügen</Checkbox.Label>}>
          <Checkbox.Checkbox onChange={addElectricityChangeHandler} />
        </Checkbox>
        {addElectricity && (
          <>
            <Stepper
              defaultValue={people}
              label="Personenanzahl"
              max={10}
              min={1}
              onChange={(value) => {
                setPeople(value);
                setElectricityUsage(String(ELECTRICITY_USAGE[Math.min(Math.max(value - 1, 0), 9)]));
              }}
            />
            <Textfield
              inputMode="numeric"
              label="Stromverbrauch"
              min={usageLimitElectricity.min}
              name="strom"
              errorMessage={`Der Verbrauch muss über ${usageLimitElectricity.min} kWh/Jahr liegen.`}
              onChange={electricityChangeHandler}
              rightSlot={<Text size="s">kWh/Jahr</Text>}
              type="number"
              value={electricityUsage}
              required
            />
          </>
        )}

        <input name="ort" type="hidden" value={city} />
        <input name="plz" type="hidden" value={zip} />
        {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
        {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}

        <BoxCTA as="button" type="submit">
          Tarife berechnen
        </BoxCTA>
      </form>
      <UsageModal isOpen={electricityUsageModalOpen} route="/gewerbestrom" setIsOpen={setElectricityUsageModalOpen} />
      <UsageModal isOpen={gasUsageModalOpen} route="/gewerbegas" setIsOpen={setGasUsageModalOpen} />
    </>
  );
};
